import React, { ReactNode, useEffect, useMemo, useState } from "react";
import { ICapability, IErrorForm, IIdName, IStorage } from "../../../../util/varibles/interface";
import { renderOpts, showErrorResponse } from "../../../../util/varibles/global";
import Menu from "../../../../components/Menu";
import Icon, { ICON_NAME } from "../../../../components/Icon";
import LoadingBox, { MessageEmpty } from "../../../../components/LoadingBox";
import { CapabilityService } from "../../../../util/services/capability";
import { EQUIPMENT_TYPE, STORAGE_GROUP_TYPE } from "../../../../util/varibles/constants";
import { callEventEditVessel, setEditVessel, useEditVessel } from "../../../../contexts/EditVesselContext";
import { Button } from "antd";
import { capabilityKeys } from "../constants";
import InputForm from "../../../../components/Basic/InputForm";
import styles from "../style.module.scss";
import ButtonDelete from "../../../../components/Basic/ButtonDelete";

const Option: React.FC<{ data: ICapability, View?: React.FC<{ onClick: () => void }> }> = ({ data, View }) => {
    const { type, name } = data;
    const set = setEditVessel();
    const onClick = useMemo(() => {
        const click: { [id: string]: (value: ICapability) => void } = {
            [EQUIPMENT_TYPE.TANK]: (value) => {
                set(({ vessel }) => {
                    const { storages = [] } = vessel || {};
                    return {
                        vessel: {
                            ...vessel,
                            storages: [
                                ...storages,
                                { type: value.type, name: '', group_type: STORAGE_GROUP_TYPE.SERVICE }
                            ]
                        }
                    }
                })
            },
            [EQUIPMENT_TYPE.CARGO]: (value) => {
                set(({ vessel }) => {
                    const { storages = [] } = vessel || {};
                    return {
                        vessel: {
                            ...vessel,
                            storages: [
                                ...storages,
                                { type: value.type, name: '', group_type: STORAGE_GROUP_TYPE.SERVICE }
                            ]
                        }
                    }
                })
            }
        }
        return click[type] || ((value: ICapability) => {
            set(({ vessel }) => {
                const { capabilities = [] } = vessel || {};
                return {
                    vessel: {
                        ...vessel,
                        capabilities: [
                            ...capabilities,
                            { type: value.type, name: '', group_type: value.group_type }
                        ]
                    }
                }
            })
        })
    }, [type, set]);

    return View
        ? <View onClick={() => onClick(data)} />
        : <div className='menu-line' onClick={() => onClick(data)}>
            <div className='capitalize'>{name}</div>
        </div>
}

const Capability: React.FC<{ data: ICapability, children: ReactNode }> = ({ data, children }) => {
    return <div className='flex flex-col gap-2'>
        <div className='flex gap-2 items-center'>
            <div className='capitalize fill-bg-second'>{data.name}</div>
            <Option
                data={data}
                View={({ onClick }) => <Button className="bt-ghost" data-icon={true} size='small' onClick={onClick}>
                    <Icon icon={ICON_NAME.ADD_2} />
                </Button>}
            />
        </div>
        <div className='flex flex-col'>
            {children}
        </div>
    </div>
}

const Capabilities: React.FC<{ data: ICapability[] }> = ({ data }) => {
    const [storages] = useEditVessel(state => state.vessel?.storages);
    const [capabilities] = useEditVessel(state => state.vessel?.capabilities);

    const groupByType = useMemo(() => {
        const result: { [id: string]: any[] } = {};
        (storages || []).forEach((item, index) => {
            if (item.group_type === STORAGE_GROUP_TYPE.SERVICE) {
                const id = item.type;
                result[id] = [...result[id] || [], { ...item, index }];
            }
        });
        (capabilities || []).forEach((item, index) => {
            const id = item.type;
            result[id] = [...result[id] || [], { ...item, index }];
        });

        return result
    }, [storages, capabilities]);

    return <div className='flex flex-col gap-3'>
        {data.map(item => {
            const id = item.type;
            const source = groupByType[id] || [];
            if (source.length > 0) {
                return <Capability key={item.type} data={item}>
                    {source.map((sub, i) => <div key={i} className='grid grid-cols-3 gap-2 relative'>
                        <CapabilityInput data={sub} />
                    </div>)}
                </Capability>
            }
            return null
        })}
    </div>
}

const CapabilityInput: React.FC<{ data: ({ index: number } & ICapability) }> = ({ data }) => {
    const event = callEventEditVessel();
    const { type } = data;
    const { form = '', actions, errorKey = '' } = capabilityKeys[type] || {};
    const [error = {}] = useEditVessel(state => {
        const { [data.index]: selector } = (state.error?.[errorKey]) || {};
        return selector
    })

    if (!form)
        return null;

    const [updateKey, deleteKey] = actions;
    const { [updateKey]: onUpdate, [deleteKey]: onDelete } = event as any;

    return <>
        {Object.keys(form).map((key) => {
            const { type: formType, ...formProps } = form[key];
            const { [formType]: Input } = InputForm;
            const { [key]: value }: any = data;
            return key === 'materials'
                ? <ExternalInput key='materials' data={data} error={error[key]} />
                : <Input
                    {...formProps}
                    inputClassName='h-[36px]'
                    key={key}
                    error={error[key]}
                    value={value}
                    onChange={value => onUpdate?.(data.index, { [key]: value })}
                />
        })}
        <ButtonDelete key='remove' className={styles.btRemove} onDelete={() => onDelete?.(data.index)} />
    </>
}

const ExternalInput: React.FC<{ data: ({ index: number } & IStorage), error?: IErrorForm }> = ({ data, error }) => {
    const [{ loading, data: materialOpts }] = useEditVessel(state => state.materials);
    const { updateStorage } = callEventEditVessel();
    const { materials = [] } = data;

    return <InputForm.Select
        label='Materials'
        className='select-mode-tag'
        mode='multiple'
        error={error}
        loading={loading}
        options={renderOpts(materialOpts)}
        value={materials.map((item: IIdName) => item.id)}
        onSelect={(_, opt) => updateStorage(data.index, {
            materials: [...materials, opt.root]
        })}
        onDeselect={(value) => updateStorage(data.index, {
            materials: materials.filter((item: IIdName) => item.id !== value)
        })}
    />
}

const ServiceCapabilities: React.FC = () => {
    const [{ loading, capabilities }, setCapabilities] = useState<{ loading: boolean, capabilities: ICapability[] }>({
        loading: false,
        capabilities: []
    });

    useEffect(() => {
        setCapabilities(prev => ({ ...prev, loading: true }));
        CapabilityService.gets()
            .then(rs => setCapabilities({ capabilities: rs, loading: false }))
            .catch(error => {
                showErrorResponse('Get capabilities failed', error);
                setCapabilities(prev => ({ ...prev, loading: false }))
            });
    }, []);

    const menus = useMemo(() => capabilities.map(item => ({
        key: item.id,
        label: <Option data={item} />
    })), [capabilities]);

    return <div className='mt-[18px]'>
        <Menu disabled={loading} items={menus}>
            <div
                className='flex gap-2 items-center text-[15px] mb-2 font-[600] cursor-pointer leading-[28px] w-[190px]'>
                <span className='whitespace-nowrap'>Add service equipment</span>
                {loading
                    ? <LoadingBox loading={true} spinnerSize={16} />
                    : <Icon icon={ICON_NAME.ADD_2} size={13} bg='colorMain' />}
            </div>
        </Menu>
        <Capabilities data={capabilities} />
        {(capabilities.length === 0 && !loading) && <MessageEmpty />}
    </div>;
};

export default ServiceCapabilities;
